import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { asset, WHATSAPP_URL } from "./GuestSite";

const doctors = [
  {
    name: "Dr. Bartholomew Onogwu", role: "Medical doctor",
    image: "doctor-bartholomew.webp", position: "50% 28%",
    bio: "Medical doctor and co-founder of Zaviramed, helping make doctor-led care more accessible through technology.",
  },
  {
    name: "Dr. Justice Sephke", role: "Medical doctor",
    image: "doctor-justice.webp", position: "57% 31%",
    bio: "Medical doctor, full-stack developer and founder of Zaviramed. Bringing healthcare and technology together to help people access medical guidance through WhatsApp.",
  },
];

export default function DoctorsCarousel() {
  const [active, setActive] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const touch = useRef(null);
  const dialog = useRef(null);
  const opener = useRef(null);
  const next = (active + 1) % doctors.length;
  const move = (direction) => setActive((value) => (value + direction + doctors.length) % doctors.length);

  useEffect(() => {
    if (showAll) dialog.current?.showModal();
    else if (dialog.current?.open) dialog.current.close();
  }, [showAll]);

  const close = () => {
    setShowAll(false);
    opener.current?.focus();
  };

  return (
    <section className="zv-doctors" id="doctors" aria-labelledby="doctors-heading">
      <div className="zv-container">
        <h2 id="doctors-heading">Meet Our Doctors</h2>
        <p className="zv-doctors-intro">Meet the team whose dedication and professionalism brings<br className="zv-doctors-break" /> health care to your doorstep.</p>
        <div className="zv-doctors-slider" role="group" aria-label="Our doctors" aria-roledescription="carousel"
          onKeyDown={(event) => {
            if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
              event.preventDefault(); move(event.key === "ArrowRight" ? 1 : -1);
            }
          }}
          onTouchStart={(event) => { touch.current = { x: event.touches[0].clientX, y: event.touches[0].clientY }; }}
          onTouchEnd={(event) => {
            if (!touch.current) return;
            const dx = touch.current.x - event.changedTouches[0].clientX;
            const dy = touch.current.y - event.changedTouches[0].clientY;
            if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) move(dx > 0 ? 1 : -1);
            touch.current = null;
          }}>
          {[active, next].map((index, slot) => {
            const doctor = doctors[index];
            const selected = active === index;
            return (
              <article key={slot} className={`zv-doctor-card ${selected ? "is-active" : "is-preview"}`}
                style={{ order: selected ? 0 : 1, "--doctor-position": doctor.position }}
                aria-label={`${doctor.name}${selected ? ", selected profile" : ", next profile"}`}>
                <div key={doctor.name} className="zv-doctor-identity" aria-hidden={!selected}>
                  <img src={asset(doctor.image)} alt={doctor.name} loading="lazy" />
                  <h3>{doctor.name}</h3>
                  <p>{doctor.role}</p>
                  <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" tabIndex={selected ? 0 : -1}>Chat with us</a>
                </div>
                <p key={`bio-${doctor.name}`} className="zv-doctor-bio" aria-hidden={!selected}>{doctor.bio}</p>
                {!selected && <button type="button" className="zv-doctor-preview-button" onClick={() => setActive(index)} aria-label={`Show ${doctor.name}`}>
                  <span><strong>{doctor.name}</strong><small>{doctor.role}</small></span>
                </button>}
              </article>
            );
          })}
        </div>
        <div className="zv-doctors-controls">
          <button type="button" onClick={() => move(-1)} aria-label="Previous doctor"><ChevronLeft size={20} /></button>
          <button type="button" className="zv-doctors-see-all" ref={opener} onClick={() => setShowAll(true)}>See all doctors <ChevronRight size={18} /></button>
          <button type="button" onClick={() => move(1)} aria-label={`Next doctor: ${doctors[next].name}`}><ChevronRight size={20} /></button>
        </div>
        <p className="zv-doctors-status" aria-live="polite" aria-atomic="true">{doctors[active].name}, {active + 1} of {doctors.length}</p>
        <dialog className="zv-doctors-dialog" ref={dialog} aria-labelledby="all-doctors-title" onCancel={close} onClose={close}
          onClick={(event) => { if (event.target === event.currentTarget) close(); }}>
          <div className="zv-doctors-dialog-content">
            <button type="button" className="zv-doctors-close" aria-label="Close doctors" onClick={close}><X size={24} /></button>
            <h2 id="all-doctors-title">Our doctors</h2>
            <div className="zv-doctors-directory">
              {doctors.map((doctor) => <article key={doctor.name}>
                <img src={asset(doctor.image)} alt={doctor.name} style={{ objectPosition: doctor.position }} />
                <h3>{doctor.name}</h3><p>{doctor.role}</p><p>{doctor.bio}</p>
              </article>)}
            </div>
          </div>
        </dialog>
      </div>
    </section>
  );
}
